import React, {useMemo, useState} from "react";
import {isMobile, isMobileOnly} from "react-device-detect";
import {parsePhoneNumberWithError} from "libphonenumber-js";
import {
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    Divider,
    IconButton,
    Typography
} from "@mui/material";
import {
    AddCircle as AddIcon,
    CalendarMonth as CalendarIcon,
    Delete as DeleteIcon,
    Directions as MapIcon,
    Edit as EditIcon,
    Email as EmailIcon,
    Link as UrlIcon,
    Phone as CallIcon,
    RemoveCircle as RemoveIcon,
    Sms as SmsIcon,
} from "@mui/icons-material";
import {ClosableDialogTitle} from "@atttomyx/react-components";
import {
    LabeledAddressForm,
    LabeledDateForm,
    LabeledEmailForm,
    LabeledPhoneForm,
    LabeledStringForm,
    LabeledUrlForm,
    NodeForm
} from "../forms";
import {nodeService} from "../../services";
import {arrays, datetime, forms, objects, users} from "@atttomyx/shared-utils";
import {confirm} from "@atttomyx/react-utils";
import "./editNodeDialog.css";
import {nodeUtils} from "../../utils";

const BOX_STYLE = {
    display: "flex",
    flexDirection: "column",
    textAlign: "left"
};

const formatPhone = (raw) => {
    const phone = parsePhoneNumberWithError(raw, "US");

    return phone.formatNational();
};

const addressToItem = (address) => {
    return {
        label: address.label,
        line1: address.line1,
        line2: address.line2,
        locality: address.locality,
        region: address.region,
        postalCode: address.postalCode,
        country: address.country,
    }
};

const addressToLines = (address) => {
    const lines = [];

    if (address.line1) {
        arrays.addTo(lines, address.line1);
    }

    if (address.line2) {
        arrays.addTo(lines, address.line2);
    }

    if (address.locality && address.region && address.postalCode) {
        arrays.addTo(lines, `${address.locality}, ${address.region} ${address.postalCode}`);

    } else if (address.locality && address.region) {
        arrays.addTo(lines, `${address.locality}, ${address.region}`);

    } else if (address.locality && address.postalCode) {
        arrays.addTo(lines, `${address.locality}, ${address.postalCode}`);

    } else if (address.region && address.postalCode) {
        arrays.addTo(lines, `${address.region} ${address.postalCode}`);

    } else if (address.locality) {
        arrays.addTo(lines, address.locality);

    } else if (address.region) {
        arrays.addTo(lines, address.region);

    } else if (address.postalCode) {
        arrays.addTo(lines, address.postalCode);
    }

    if (address.country) {
        arrays.addTo(lines, address.country);
    }

    return lines;
};

const toData = (node) => {
    return {
        imageUrl: node.imageUrl,
        firstName: node.firstName,
        lastName: node.lastName,
        phones: arrays.copy(node.phones),
        emails: arrays.copy(node.emails),
        urls: arrays.copy(node.urls),
        addresses: arrays.copy(node.addresses),
        dates: arrays.copy(node.dates),
        others: arrays.copy(node.others),
        valid: true,
    }
};

const InlineAdd = (props) => {
    const { label, onClick } = props;

    return (
        <div className="inline-add">
            <IconButton
                color="primary"
                variant="text"
                onClick={onClick}
            >
                <AddIcon/>
            </IconButton>
            <Typography
                className="item"
                variant="caption"
                color="text.primary"
            >
                Add {label}
            </Typography>
        </div>
    )
};

const InlineActions = (props) => {
    const { disabled, onCancel, onOk } = props;

    return (
        <div className="inline-actions">
            <Button
                variant="text"
                color="secondary"
                className="delete"
                onClick={onCancel}
            >
                Cancel
            </Button>
            <Button
                variant="text"
                color="primary"
                disabled={disabled}
                onClick={onOk}
            >
                Ok
            </Button>
        </div>
    )
};

const EditNodeDialog = (props) => {
    const {snackbar, user, node, showActions, onCancel, onSaveNode, onDeleteNode} = props;
    const [data, setData] = useState(toData(node));
    const [phone, setPhone] = useState(null);
    const [email, setEmail] = useState(null);
    const [url, setUrl] = useState(null);
    const [address, setAddress] = useState(null);
    const [date, setDate] = useState(null);
    const [other, setOther] = useState(null);
    const [spinner, setSpinner] = useState(false);

    const root = useMemo(() => {
        return nodeUtils.isRoot(node, user);
    }, [node, user]);

    const failure = (err) => {
        snackbar.setError(err);
        setSpinner(false);
    };

    const deleteNode = () => {
        setSpinner(true);

        const success = (deletedId) => {
            setSpinner(false);
            onDeleteNode(deletedId);
        };

        nodeService.deleteNode(node.id, success, failure);
    };

    const clearForm = () => {
        setData(toData(node));
        setPhone(null);
        setEmail(null);
        setUrl(null);
        setAddress(null);
        setDate(null);
        setOther(null);
    };

    const submitForm = () => {
        setSpinner(true);

        const success = (saved) => {
            setSpinner(false);
            onSaveNode(saved);
        };

        const modified = {
            imageUrl: data.imageUrl,
            firstName: data.firstName,
            lastName: data.lastName,
            phones: data.phones,
            emails: data.emails,
            urls: data.urls,
            addresses: data.addresses,
            dates: data.dates,
            others: data.others,
            metadata: node.metadata,
        };

        nodeService.updateNode(node.id, modified, success, failure);
    };

    const editItem = (item, index, setter) => {
        const decorated = objects.deepCopy(item);

        decorated.index = index;
        setter(decorated);
    };

    const removeItem = (field, index) => {
        const modified = objects.deepCopy(data);
        const array = arrays.copy(data[field]);

        array.splice(index, 1);
        modified[field] = array;

        setData(modified);
    };

    const insertItem = (field, form, setter, transformer) => {
        const modified = objects.deepCopy(data);
        const array = arrays.copy(data[field]);

        const item = transformer
            ? transformer(form)
            : {
                label: form.label,
                value: form.value,
            };

        if (objects.notNullOrUndefined(form.index)) {
            array.splice(form.index, 1, item);

        } else {
            arrays.addTo(array, item);
        }

        modified[field] = array;

        setData(modified);
        setter(null);
    };

    const renderItems = (label, field, setter, formatter) => {
        return data[field].map((item, index) => (
            <div key={index} className="row">
                <IconButton
                    color="secondary"
                    title={`Remove ${label}`}
                    onClick={() => removeItem(field, index)}
                >
                    <RemoveIcon/>
                </IconButton>
                <Box sx={BOX_STYLE} className="item">
                    <Typography variant="caption" color="text.secondary" className="truncated">
                        {item.label}
                    </Typography>
                    <Typography className="truncated">
                        {formatter ? formatter(item.value) : item.value}
                    </Typography>
                </Box>
                <IconButton
                    className="right"
                    color="primary"
                    title="Edit"
                    onClick={() => editItem(item, index, setter)}
                >
                    <EditIcon/>
                </IconButton>
            </div>
        ));
    };

    const renderMultiFieldItems = (label, field, setter, transformer) => {
        return data[field].map((item, index) => (
            <div key={index} className="row">
                <IconButton
                    color="secondary"
                    title={`Remove ${label}`}
                    onClick={() => removeItem(field, index)}
                >
                    <RemoveIcon/>
                </IconButton>
                <Box sx={BOX_STYLE} className="item">
                    <Typography variant="caption" color="text.secondary" className="truncated">
                        {item.label}
                    </Typography>
                    {transformer(item).map((line, index) => (
                        <Typography key={index} className="truncated">
                            {line}
                        </Typography>
                    ))}
                </Box>
                <IconButton
                    className="right"
                    color="primary"
                    title="Edit"
                    onClick={() => editItem(item, index, setter)}
                >
                    <EditIcon/>
                </IconButton>
            </div>
        ));
    };

    const renderPhones = () => {
        return phone ?
            <>
                <LabeledPhoneForm
                    data={phone}
                    onChange={setPhone}
                />
                <InlineActions
                    onCancel={() => setPhone(null)}
                    onOk={() => insertItem("phones", phone, setPhone)}
                    disabled={!phone.valid}
                />
            </> :
            <>
                {renderItems("phone", "phones", setPhone, formatPhone)}
                <div className="row">
                    <InlineAdd
                        label="phone"
                        onClick={() => setPhone({})}
                    />
                </div>
            </>
    };

    const renderEmails = () => {
        return email ?
            <>
                <LabeledEmailForm
                    data={email}
                    onChange={setEmail}
                />
                <InlineActions
                    onCancel={() => setEmail(null)}
                    onOk={() => insertItem("emails", email, setEmail)}
                    disabled={!email.valid}
                />
            </> :
            <>
                {renderItems("email", "emails", setEmail)}
                <div className="row">
                    <InlineAdd
                        label="email"
                        onClick={() => setEmail({})}
                    />
                </div>
            </>
    };

    const renderUrls = () => {
        return url ?
            <>
                <LabeledUrlForm
                    data={url}
                    onChange={setUrl}
                />
                <InlineActions
                    onCancel={() => setUrl(null)}
                    onOk={() => insertItem("urls", url, setUrl)}
                    disabled={!url.valid}
                />
            </> :
            <>
                {renderItems("URL", "urls", setUrl)}
                <div className="row">
                    <InlineAdd
                        label="URL"
                        onClick={() => setUrl({})}
                    />
                </div>
            </>
    };

    const renderAddresses = () => {
        return address ?
            <>
                <LabeledAddressForm
                    data={address}
                    onChange={setAddress}
                />
                <InlineActions
                    onCancel={() => setAddress(null)}
                    onOk={() => insertItem("addresses", address, setAddress, addressToItem)}
                    disabled={!address.valid}
                />
            </> :
            <>
                {renderMultiFieldItems("address", "addresses", setAddress, addressToLines)}
                <div className="row">
                    <InlineAdd
                        label="address"
                        onClick={() => setAddress({})}
                    />
                </div>
            </>
    };

    const renderDates = () => {
        return date ?
            <>
                <LabeledDateForm
                    data={date}
                    onChange={setDate}
                />
                <InlineActions
                    onCancel={() => setDate(null)}
                    onOk={() => insertItem("dates", date, setDate)}
                    disabled={!date.valid}
                />
            </> :
            <>
                {renderItems("date", "dates", setDate, datetime.getFullDate)}
                <div className="row">
                    <InlineAdd
                        label="date"
                        onClick={() => setDate({})}
                    />
                </div>
            </>
    };

    const renderOthers = () => {
        return other ?
            <>
                <LabeledStringForm
                    data={other}
                    onChange={setOther}
                />
                <InlineActions
                    onCancel={() => setOther(null)}
                    onOk={() => insertItem("others", other, setOther)}
                    disabled={!other.valid}
                />
            </> :
            <>
                {renderItems("other", "others", setOther)}
                <div className="row">
                    <InlineAdd
                        label="other"
                        onClick={() => setOther({})}
                    />
                </div>
            </>
    };

    return <Dialog
        open={true}
        aria-labelledby="edit-node-dialog"
        className="tall"
    >
        <ClosableDialogTitle onClose={onCancel}>
            Edit Person
        </ClosableDialogTitle>
        <DialogContent className="edit-node-dialog">
        {spinner ?
                <CircularProgress size="40px"/> :
                <>
                    <NodeForm
                        snackbar={snackbar}
                        nodeId={node.id}
                        data={data}
                        onChange={setData}
                    />
                    {showActions ?
                        <>
                            <Divider/>
                            <div className="button-row">
                                <IconButton
                                    color="primary"
                                    title="Text"
                                    disabled={!isMobile || root || data.phones.length === 0}
                                    onClick={() => alert("text")}
                                >
                                    <SmsIcon/>
                                </IconButton>
                                <IconButton
                                    color="primary"
                                    title="Call"
                                    disabled={!isMobileOnly || root || data.phones.length === 0}
                                    onClick={() => alert("call")}
                                >
                                    <CallIcon/>
                                </IconButton>
                                <IconButton
                                    color="primary"
                                    title="Email"
                                    disabled={root || data.emails.length === 0}
                                    onClick={() => alert("email")}
                                >
                                    <EmailIcon/>
                                </IconButton>
                                <IconButton
                                    color="primary"
                                    title="Goto URL"
                                    disabled={data.urls.length === 0}
                                    onClick={() => alert("url")}
                                >
                                    <UrlIcon/>
                                </IconButton>
                                <IconButton
                                    color="primary"
                                    title="Directions"
                                    disabled={data.addresses.length === 0}
                                    onClick={() => alert("directions")}
                                >
                                    <MapIcon/>
                                </IconButton>
                                <IconButton
                                    color="primary"
                                    title="Goto calendar"
                                    disabled={data.dates.length === 0}
                                    onClick={() => alert("calendar")}
                                >
                                    <CalendarIcon/>
                                </IconButton>
                            </div>
                        </> : null}
                    <Divider/>
                    {renderPhones()}
                    <Divider/>
                    {renderEmails()}
                    <Divider/>
                    {renderUrls()}
                    <Divider/>
                    {renderAddresses()}
                    <Divider/>
                    {renderDates()}
                    <Divider/>
                    {renderOthers()}
                </>}
        </DialogContent>
        {!spinner ? <DialogActions>
            {showActions ?
                <IconButton
                    color="secondary"
                    title="Delete"
                    className="delete"
                    disabled={root}
                    onClick={() => confirm.confirmDelete(users.getFullName(node), deleteNode)}
                >
                    <DeleteIcon/>
                </IconButton> : null}
            <Button
                color="secondary"
                variant="text"
                disabled={!forms.differ(toData(node), data)}
                onClick={clearForm}
            >
                Undo
            </Button>
            <Button
                color="primary"
                disabled={!data.valid || !forms.differ(toData(node), data)}
                onClick={submitForm}
            >
                Save
            </Button>
        </DialogActions> : null}
    </Dialog>
}

export default EditNodeDialog;
